import React from "react";
import type { RouteComponentProps } from "react-router";
import { Route, type RouteProps } from "react-router-dom";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import styles from "./css/routing.module.css";

type ProtectedRouteProps<T> = RouteProps &
	T & {
		authorized: boolean;
		fail?: (props: RouteComponentProps) => React.ReactNode;
	};

const Fail: React.FunctionComponent = () => {
	return (
		<div className={styles.fail}>
			<FontAwesomeIcon icon={faInfoCircle} color="orange" size="4x" />
			<h1 className={styles.header}>Access Denied</h1>
			<div className={styles.subheader}>You do not have permission to view this page.</div>
		</div>
	);
};

export function ProtectedRoute<T>({
	authorized,
	fail,
	render,
	component: Component,
	...rest
}: ProtectedRouteProps<T>): React.ReactElement<ProtectedRouteProps<T>> {
	const renderFail = (props: RouteComponentProps) => {
		return fail ? fail(props) : <Fail />;
	};

	const renderSuccess = (props: RouteComponentProps) => {
		if (render) return render(props);
		if (Component) return <Component {...props} />;

		throw new Error(`One of "render" or "component" must be passed in to a ProtectedRoute`);
	};

	const renderRouter = (props: RouteComponentProps) => {
		return authorized ? renderSuccess(props) : renderFail(props);
	};

	// eslint-disable-next-line react/jsx-no-bind
	return <Route {...rest} render={renderRouter} />;
}

// Example usage
// <ProtectedRoute<DefaultPropsType>
// 	fail={props => <div />}
// 	authorized={user.isAccountAdmin || user.isManager}
// 	path="/dashboard"
// 	component={Test}
//			OR
// 	{...Test.getRouteProps<DefaultPropsType>(defaultProps)}
// />;
